import React from "react";
import {faCheck, faCogs, faFile, faFilter, faSearch} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {Badge, ButtonGroup, Col, Container, Dropdown, DropdownButton, Row, ToggleButton} from "react-bootstrap";
import { Comparator, dateFilter, selectFilter, textFilter } from "react-bootstrap-table2-filter";
import { withTranslation } from "react-i18next";
import PaginationTable from "../../../../components/paginationTable/PaginationTable";
import APIErrorHelper from "../../../../helpers/APIErrorHelper";
import CamplaCoreService from "../../../../services/CamplaCoreService";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import ExaminationRestrictionsDialog from "./examinationRestrictionsDialog/ExaminationRestrictionsDialog";
import StudentFileProposalsAcceptedCell from "./studentFileProposalsAcceptedCell/StudentFileProposalsAcceptedCell";
import StudentFileProposalsActionCell from "./studentFileProposalsActionCell/StudentFileProposalsActionCell";
import StorageService from "../../../../services/StorageService";
import ConfirmationDialog from "../../../../components/dialogs/confirmationDialog/ConfirmationDialog";

import "./StudentFileProposalsTab.css";
import RunningDownloadDialog from "../../../../components/dialogs/runningDownloadDialog/RunningDownloadDialog";
import PaginationTableFilterToggle
  from "../../../../components/paginationTableFilterToggle/PaginationTableFilterToggle";

class StudentFileProposalsTab extends React.Component {

  constructor(props) {
    super(props);
    this.tableId = "studentsFileProposalsTabTable";
    let showFilters = StorageService.tableConfigShowFilters(this.tableId);
    this.state = {
      displayFilter: showFilters ? "initial" : "none",
      showFilters,
      showRestrictionsDialog: false,
      showConfirmationDialog: false,
      confirmationDialogRunning: false,
      restrictions: undefined,
      defaultRestrictions: undefined,
      dialogApiError: "",
      apiError: "",
      runningDownloadDialogShow: false,
      runningDownloadDialogTitle: "",
      runningDownloadDialogProgress: 0
    };
    this.handleAcceptProposal = this.handleAcceptProposal.bind(this);
    this.handleRejectProposal = this.handleRejectProposal.bind(this);
    this.handleAcceptAllProposals = this.handleAcceptAllProposals.bind(this);
    this.handleDownloadStudentFileProposal = this.handleDownloadStudentFileProposal.bind(this);
  }

  async componentDidMount() {
    await this.fetchData();
    CamplaCoreService.addEventListener("StudentFileProposalsTab", async event => {
      if (event.model === "studentFileStorage") {
        if (event.object || !event.object.isExaminationFile) {
          await this.fetchData();
        }
      }
    });
  }

  async fetchData() {
    try {
      const restrictions = await CamplaCoreService.getExaminationRestrictions(
          this.props.examination._id
      );
      const defaultRestrictions = await CamplaCoreService.getDefaultRestrictions(
          this.props.examination._id
      );
      this.setState({
        restrictions,
        defaultRestrictions
      });
    }catch (err){
      if (typeof err["status"] !== "undefined" && err.status !== 404){
        this.setState({
          apiError: APIErrorHelper.getError(
              err,
              () => this.setState({ apiError: "" }),
              {},
              this.props.t
          )
        });
      }
    }
  }

  humanReadableFileSize(size) {
    const labels = ["B", "KB", "MB", "GB"];
    let ct = 0;
    while (size > 1024 && ct < labels.length) {
      size /= 1024;
      ct++;
    }
    return `${Math.round(size * 100) / 100} ${labels[ct]}`;
  }

  async handleUpdateRestrictions(changedRestrictions, t) {
    changedRestrictions.maxTotalSize *= 1024 * 1024; // MB to Bytes
    changedRestrictions = Object.assign(
      this.state.restrictions,
      changedRestrictions
    );
    try {
      const updatedRestrictions = await CamplaCoreService.updateExaminationRestrictions(
        this.props.examination._id,
        changedRestrictions
      );
      this.props.onUpdateRestrictions(updatedRestrictions);
      this.setState({
        showRestrictionsDialog: false,
        restrictions: updatedRestrictions
      });
    } catch (err) {
      this.setState({
        apiError: APIErrorHelper.getError(
          err,
          () => this.setState({ apiError: "" }),
          {
            PRECONDITION_FAILED:
              "SingleExamination.StudentFileProposalsTab.APIError.PreconditionFailed",
            BAD_REQUEST: "",
            NOT_FOUND: "",
            SERVER_ERROR: ""
          },
          t
        )
      });
    }
  }

  async handleAcceptProposal(studentFileId) {
    await this.props.onAcceptProposal(studentFileId);
  }

  async handleAcceptAllProposals(){
    this.setState({confirmationDialogRunning: true});
    for (const proposal of this.props.studentFileProposals){
      if (!proposal.isAccepted){
        const status = await this.props.onAcceptProposal(proposal._id);
        if (!status) {
          this.setState({confirmationDialogRunning: false});
          return;
        }
        await new Promise(resolve => setTimeout(() => resolve(), 100));
      }
    }
    this.setState({
      showConfirmationDialog: false,
      confirmationDialogRunning: false
    });
  }

  async handleRejectProposal(studentFileId) {
    await this.props.onRejectProposal(studentFileId);
  }

  async handleDownloadStudentFileProposal(studentFileProposal) {
    try {
      this.setState({
        runningDownloadDialogShow: true,
        runningDownloadDialogTitle: studentFileProposal.name,
        runningDownloadDialogProgress: 0
      });
      await CamplaCoreService.downloadStudentFile(
          studentFileProposal._id,
          studentFileProposal.name,
          percentage =>
              this.setState({ runningDownloadDialogProgress: percentage })
      );
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0
      });
    } catch (err) {
      //Todo: Add correct errors according to documentation
      this.setState({
        runningDownloadDialogShow: false,
        runningDownloadDialogTitle: "",
        runningDownloadDialogProgress: 0,
        dialogApiError: APIErrorHelper.getError(
            err,
            () => this.setState({ dialogApiError: "" }),
            {
              PRECONDITION_FAILED:
                  "SingleExamination.StudentFileProposalsTab.APIError.PreconditionFailed",
              NOT_FOUND: "SingleExamination.StudentFileProposalsTab.APIError.NotFound",
              BAD_REQUEST: "SingleExamination.StudentFileProposalsTab.BadRequest"
            },
            this.props.t
        )
      });
    }
  }

  render() {
    const { t } = this.props;
    const actionCellData = {
      examinationStatus: this.props.examination.status
    };
    const columns = [
      {
        dataField: "status",
        text: "",
        isDummyField: true,
        csvExport: false,
        headerStyle: () => ({ width: "50px" }),
        formatExtraData: actionCellData,
        formatter: (cell, proposal) => proposal.isAccepted ?  (
            <FontAwesomeIcon icon={faCheck} className="color-green"/>
        ) : (<span className="fa-layers fa-fw" key={1}>
        <FontAwesomeIcon icon={faFile} size="xs" className="color-red" />
        <FontAwesomeIcon icon={faSearch} size="lg" className="color-black" />
      </span>),
        sort: false
      },
      {
        dataField: "studentName",
        headerStyle: () => ({ width: "200px" }),
        text: t("SingleExamination.StudentFileProposalsTab.ColumnStudentName"),
        sort: true,
        filter: textFilter({
          placeholder: t(
              "SingleExamination.StudentFileProposalsTab.FilterStudentNamePlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "studentEmail",
        text: t("SingleExamination.StudentFileProposalsTab.ColumnStudentEmail"),
        sort: true,
        filter: textFilter({
          placeholder: t(
            "SingleExamination.StudentFileProposalsTab.FilterStudentEmailPlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "name",
        text: t("SingleExamination.StudentFileProposalsTab.ColumnName"),
        sort: true,
        filter: textFilter({
          placeholder: t(
              "SingleExamination.StudentFileProposalsTab.FilterNamePlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "created_at",
        text: t("SingleExamination.StudentFileProposalsTab.ColumnSubmittedAt"),
        sort: true,
        formatter: cell => DateTimeHelper.formatDate(cell),
        filter: dateFilter({
          style: { display: this.state.displayFilter },
          defaultValue: { date: undefined, comparator: Comparator.EQ }
        })
      },
      {
        dataField: "isAccepted",
        text: t("SingleExamination.StudentFileProposalsTab.ColumnIsAccepted"),
        sort: true,
        formatter: cell => {
          return <StudentFileProposalsAcceptedCell isAccepted={cell} />;
        },
        filter: selectFilter({
          options: [
            {
              value: true,
              label: t("SingleExamination.StudentFileProposalsTab.IsAccepted")
            },
            {
              value: false,
              label: t(
                "SingleExamination.StudentFileProposalsTab.IsNotAccepted"
              )
            }
          ],
          placeholder: t(
            "SingleExamination.StudentFileProposalsTab.FilterStatusPlaceholder"
          ),
          style: { display: this.state.displayFilter }
        })
      },
      {
        dataField: "actions",
        text: "",
        headerFormatter: () => (
            <PaginationTableFilterToggle
                onFilterToggle={(e) => {
                  this.setState({
                    showFilters: e.currentTarget.checked,
                    displayFilter: e.currentTarget.checked ? "initial" : "none",
                  });
                }}
                showFilters={this.state.showFilters}
                tableId={this.tableId} />
        ),
        headerClasses: "actionHeader",
        isDummyField: true,
        csvExport: false,
        formatExtraData: actionCellData,
        formatter: (cell, row, idx, props) => {
          return (
            <StudentFileProposalsActionCell
              onAcceptSubmission={this.handleAcceptProposal}
              onRejectSubmission={this.handleRejectProposal}
              onDownloadSubmission={this.handleDownloadStudentFileProposal}
              examinationStatus={props.examinationStatus}
              proposal={row}
            />
          );
        }
      }
    ];
    return (
      <div>
        <ConfirmationDialog
            title={t("SingleExamination.StudentFileProposalsTab.ConfirmationDialog.Title")}
            body={t("SingleExamination.StudentFileProposalsTab.ConfirmationDialog.Question")}
            show={this.state.showConfirmationDialog}
            rightButtonIsLoading={this.state.confirmationDialogRunning}
            dialogApiError={this.state.dialogApiError}
            onHide={() => this.setState({ showConfirmationDialog: false }) }
            onSubmit={() => this.handleAcceptAllProposals()}
            confirmButtonVariant="green"
            cancelButtonVariant="black"
            confirmButtonText={t("SingleExamination.StudentFileProposalsTab.ConfirmationDialog.Accept")}
        />
        <RunningDownloadDialog
            show={this.state.runningDownloadDialogShow}
            onHide={() => this.setState({ runningDownloadDialogShow: false })}
            title={this.state.runningDownloadDialogTitle}
            progress={this.state.runningDownloadDialogProgress}
        />
        {this.state.defaultRestrictions && this.state.restrictions ? (
          <ExaminationRestrictionsDialog
            onSubmit={value => this.handleUpdateRestrictions(value, t)}
            examination={this.props.examination}
            hasFiles={this.props.studentFileProposals?.length > 0}
            show={this.state.showRestrictionsDialog}
            restrictions={this.state.restrictions}
            dialogApiError={
              this.state.dialogApiError || this.props.dialogApiError
            }
            defaultRestrictions={this.state.defaultRestrictions}
            onHide={() =>
              this.setState({
                showRestrictionsDialog: false
              })
            }
          />
        ) : null}
        <Container className="pl-0 pr-0">
          {this.state.apiError ? (
            <Row>
              <Col>{this.state.apiError}</Col>
            </Row>
          ) : (
            ""
          )}
          <Row className="mt-3">
            <Col>
              <h4>{t("SingleExamination.StudentFileProposalsTabTitle")}</h4>
            </Col>
            <Col>
              <div style={{ float: "right" }}>
                <DropdownButton
                  onClick={event => event.stopPropagation()}
                  disabled={
                    this.props.examination.status !== 1 &&
                    !(
                      this.props.examination.status >= 50 &&
                      this.props.examination.status <= 53
                    )
                  }
                  id="dropdown-basic-button"
                  title={<FontAwesomeIcon icon={faCogs} />}
                  className="btn-green"
                >
                  <Dropdown.Item
                    onClick={() => this.setState({ showRestrictionsDialog: true })}
                  >
                    {t(
                      "SingleExamination.StudentFileProposalsTab.ExaminationRestrictions"
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                      onClick={() => this.setState({ showConfirmationDialog: true })}
                  >
                    {t(
                        "SingleExamination.StudentFileProposalsTab.AcceptAll"
                    )}
                  </Dropdown.Item>
                </DropdownButton>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <p>
                {t("SingleExamination.StudentFileProposalsTab.Text")}
                <br />
              </p>
            </Col>
          </Row>          <Row>
          <Col className="mt-2">
            <h6>
              <Badge className="badge-light" style={{fontSize: 14}}>
                <FontAwesomeIcon icon={faCheck} className="color-green" /> {
                this.props.studentFileProposals.filter(i => i.isAccepted).reduce((accu) => accu + 1, 0)
              } / {
                this.props.studentFileProposals.length
              }
              </Badge>
            </h6>
          </Col>
        </Row>
          <Row className="mt-1">
            <Col>
              <PaginationTable
                data={this.props.studentFileProposals}
                sizePerPage={10}
                paginationSize={3}
                columns={columns}
                keyField="_id"
                id={this.tableId}
                noDataFoundLabel={t(
                    "SingleExamination.StudentFileProposalsTab.NoStudentFileProposalsFound"
                )}
              />
            </Col>
          </Row>
        </Container>
      </div>
    );
  }
}

export default withTranslation()(StudentFileProposalsTab);
